import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { validateContact, sanitizeText, validateEmail } from '../utils/sanitize';
import api from '../api/client';
import Turnstile, { TURNSTILE_SITE_KEY } from '../components/Turnstile';
import '../styles/profile.css';

const F = 'Cinzel,Georgia,serif';

// ── Shared layout ────────────────────────────────────────────────────────────
function PageShell({ title, subtitle, icon, children }) {
  return (
    <div style={{ minHeight: '80vh' }}>
      {/* Hero banner */}
      <div style={{
        background: 'linear-gradient(180deg,#04020c 0%,#0b0720 60%,#0a0618 100%)',
        borderBottom: '1px solid rgba(240,192,64,0.15)',
        padding: '56px 24px 44px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(240,192,64,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(240,192,64,0.03) 1px,transparent 1px)', backgroundSize: '60px 60px', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', maxWidth: 700, margin: '0 auto' }}>
          {icon && <div style={{ fontSize: 44, marginBottom: 14 }}>{icon}</div>}
          <h1 style={{ fontFamily: F, fontSize: 'clamp(24px,5vw,42px)', fontWeight: 900, color: '#ffffff', margin: '0 0 12px', letterSpacing: 3, textShadow: '0 0 40px rgba(240,192,64,0.3)' }}>
            {title}
          </h1>
          {subtitle && (
            <p style={{ color: 'rgba(210,195,250,0.65)', fontSize: 14, letterSpacing: 2, textTransform: 'uppercase', margin: 0 }}>
              {subtitle}
            </p>
          )}
        </div>
        <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '60%', height: 1, background: 'linear-gradient(90deg,transparent,rgba(240,192,64,0.3),transparent)' }} />
      </div>

      {/* Content */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '48px 24px' }}>
        {children}
      </div>
    </div>
  );
}

// ── Section heading ───────────────────────────────────────────────────────────
function SectionTitle({ children }) {
  return (
    <h2 style={{
      fontFamily: F, fontSize: 18, fontWeight: 700, color: '#f0c040',
      margin: '0 0 16px', letterSpacing: 2, textTransform: 'uppercase',
      paddingBottom: 8, borderBottom: '1px solid rgba(240,192,64,0.2)',
    }}>
      {children}
    </h2>
  );
}

// ── Gold divider ──────────────────────────────────────────────────────────────
function Divider() {
  return <div style={{ height: 1, background: 'linear-gradient(90deg,transparent,rgba(240,192,64,0.25),transparent)', margin: '36px 0' }} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// APIE MUS
// ─────────────────────────────────────────────────────────────────────────────
export function AboutPage() {
  const { t } = useTranslation();
  const [counts, setCounts] = useState({ clubs: null, events: null, players: null });

  useEffect(() => {
    Promise.all([
      api.get('/clubs',   { params: { limit: 1 } }).then(r => r.data.total ?? r.data.length),
      api.get('/events',  { params: { limit: 1 } }).then(r => r.data.total ?? r.data.length),
      api.get('/players', { params: { limit: 1 } }).then(r => r.data.total ?? r.data.length),
    ]).then(([clubs, events, players]) => setCounts({ clubs, events, players }))
      .catch(() => {});
  }, []);

  const fmt = (n) => n == null ? '...' : n > 999 ? `${(n/1000).toFixed(1).replace('.0','')}k+` : `${n}+`;

  const stats = [
    { n: fmt(counts.clubs),   label: 'Klubų' },
    { n: fmt(counts.players), label: 'Bendruomenės narių' },
    { n: fmt(counts.events),  label: 'Metinių renginių' },
    { n: '2026',              label: 'Įkūrimo metai' },
  ];

  const values = [
    { icon: '🛡️', title: 'Bendruomeniškumas', text: 'Kuriame saugią erdvę kiekvienam fantasy entuziastui rasti savo vietą ir bendraminčius.' },
    { icon: '🎲', title: 'Kūrybiškumas', text: 'Palaikome visų formų fantastikos kūrybą: nuo stalo žaidimų iki gyvojo vaidmenų žaidimo.' },
    { icon: '⚔️', title: 'Atvirumas', text: 'Kiekvienas — pradedantysis ar veteranas — čia randa savo vietą.' },
    { icon: '🌟', title: 'Įtraukimas', text: 'Fantasy pasaulis priklauso visiems. Mes puoselėjame įvairumą ir pagarbą.' },
  ];

  return (
    <PageShell title={t('pages.about.title')} subtitle={t('pages.about.subtitle')} icon="⚔️">
      {/* Mission */}
      <div style={{ marginBottom: 48 }}>
        <SectionTitle>{t('pages.about.mission_title')}</SectionTitle>
        <p style={{ color: '#ffffff', fontSize: 16, lineHeight: 1.8, margin: 0 }}>
          {t('pages.about.mission_text')}
        </p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 48 }}>
        {stats.map(({ n, label }) => (
          <div key={label} style={{
            background: 'linear-gradient(135deg,rgba(240,192,64,0.07),rgba(123,79,212,0.07))',
            border: '1px solid rgba(240,192,64,0.18)',
            borderRadius: 12, padding: '24px 16px', textAlign: 'center',
          }}>
            <div style={{ fontFamily: F, fontSize: 32, fontWeight: 900, color: '#f0c040', marginBottom: 6 }}>{n}</div>
            <div style={{ fontSize: 12, color: 'rgba(210,195,250,0.65)', letterSpacing: 1.5, textTransform: 'uppercase' }}>{label}</div>
          </div>
        ))}
      </div>

      <Divider />

      {/* Story */}
      <div style={{ marginBottom: 48 }}>
        <SectionTitle>{t('pages.about.story_title')}</SectionTitle>
        <p style={{ color: 'rgba(230,225,255,0.85)', fontSize: 15, lineHeight: 1.8, marginBottom: 16 }}>
          Fantastai.lt gimė 2019 metais Vilniuje iš nedidelės entuziastų grupės, kurie suprato, kad Lietuvoje trūksta vienos centralizuotos platformos fantasy, LARP, RPG ir CCG bendruomenei.
        </p>
        <p style={{ color: 'rgba(230,225,255,0.85)', fontSize: 15, lineHeight: 1.8, margin: 0 }}>
          Šiandien esame auganti bendruomenė su šimtais aktyvių narių, keliasdešimčia registruotų klubų ir reguliariais renginiais visoje Lietuvoje.
        </p>
      </div>

      <Divider />

      {/* Values */}
      <SectionTitle>{t('pages.about.values_title')}</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 16 }}>
        {values.map(({ icon, title, text }) => (
          <div key={title} style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 12, padding: '20px 22px',
          }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>{icon}</div>
            <h3 style={{ fontFamily: F, fontSize: 14, color: '#ffffff', margin: '0 0 8px', letterSpacing: 1 }}>{title}</h3>
            <p style={{ color: 'rgba(210,195,250,0.7)', fontSize: 13, lineHeight: 1.6, margin: 0 }}>{text}</p>
          </div>
        ))}
      </div>
    </PageShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// KONTAKTAI
// ─────────────────────────────────────────────────────────────────────────────
export function ContactPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '', honeypot: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null); // 'sending' | 'ok' | 'err'
  const [captcha, setCaptcha] = useState('');
  const captchaResetRef = useRef(null);
  const captchaOk = !TURNSTILE_SITE_KEY || !!captcha;

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { ok, errors: errs } = validateContact(form);
    if (!ok) { setErrors(errs); return; }
    if (!captchaOk) return;
    setErrors({});
    setStatus('sending');
    try {
      await api.post('/contact', {
        name:     sanitizeText(form.name, 100),
        email:    form.email.trim().toLowerCase(),
        subject:  sanitizeText(form.subject, 200),
        message:  sanitizeText(form.message, 2000),
        honeypot: form.honeypot,
        turnstile_token: captcha,
      });
      setStatus('ok');
      setForm({ name: '', email: '', subject: '', message: '', honeypot: '' });
    } catch {
      setStatus('err');
    } finally {
      captchaResetRef.current?.();
    }
  };

  const inputStyle = (hasErr) => ({
    width: '100%', background: 'rgba(255,255,255,0.04)',
    border: `1px solid ${hasErr ? '#ff8080' : 'rgba(255,255,255,0.1)'}`,
    borderRadius: 8, color: '#fff', padding: '11px 14px', fontSize: 14,
    outline: 'none', boxSizing: 'border-box',
    transition: 'border-color .15s',
    fontFamily: 'Inter,sans-serif',
  });

  const socials = [
    { icon: '📘', label: 'Facebook', href: 'https://facebook.com/fantastai.lt' },
    { icon: '📸', label: 'Instagram', href: 'https://instagram.com/fantastai.lt' },
    { icon: '💬', label: 'Discord', href: '#' },
    { icon: '▶️', label: 'YouTube', href: '#' },
  ];

  return (
    <PageShell title={t('pages.contact.title')} subtitle={t('pages.contact.subtitle')} icon="✉️">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 40 }}>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate>
          {status === 'ok' && (
            <div style={{ background: 'rgba(80,200,100,0.1)', border: '1px solid rgba(80,200,100,0.3)', borderRadius: 8, padding: '14px 18px', marginBottom: 24, color: '#80e090', fontSize: 14 }}>
              ✅ {t('pages.contact.form_success')}
            </div>
          )}
          {status === 'err' && (
            <div style={{ background: 'rgba(255,80,80,0.1)', border: '1px solid rgba(255,80,80,0.3)', borderRadius: 8, padding: '14px 18px', marginBottom: 24, color: '#ff8080', fontSize: 14 }}>
              ⚠️ {t('pages.contact.form_error')}
            </div>
          )}

          {/* Honeypot — hidden from real users, catches bots */}
          <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
            <label htmlFor="hp_field">{t('pages.contact.honeypot_label')}</label>
            <input id="hp_field" type="text" tabIndex={-1} autoComplete="off" value={form.honeypot} onChange={set('honeypot')} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', color: 'rgba(210,195,250,0.75)', fontSize: 12, letterSpacing: 1, marginBottom: 6, fontFamily: F }}>
                {t('pages.contact.form_name')} *
              </label>
              <input
                type="text" value={form.name} onChange={set('name')}
                maxLength={100} autoComplete="name"
                style={inputStyle(errors.name)}
                onFocus={e => e.target.style.borderColor = 'rgba(240,192,64,0.5)'}
                onBlur={e => e.target.style.borderColor = errors.name ? '#ff8080' : 'rgba(255,255,255,0.1)'}
              />
              {errors.name && <div style={{ color: '#ff8080', fontSize: 11, marginTop: 4 }}>{errors.name}</div>}
            </div>

            <div>
              <label style={{ display: 'block', color: 'rgba(210,195,250,0.75)', fontSize: 12, letterSpacing: 1, marginBottom: 6, fontFamily: F }}>
                {t('pages.contact.form_email')} *
              </label>
              <input
                type="email" value={form.email} onChange={set('email')}
                maxLength={254} autoComplete="email"
                style={inputStyle(errors.email)}
                onFocus={e => e.target.style.borderColor = 'rgba(240,192,64,0.5)'}
                onBlur={e => e.target.style.borderColor = errors.email ? '#ff8080' : 'rgba(255,255,255,0.1)'}
              />
              {errors.email && <div style={{ color: '#ff8080', fontSize: 11, marginTop: 4 }}>{errors.email}</div>}
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', color: 'rgba(210,195,250,0.75)', fontSize: 12, letterSpacing: 1, marginBottom: 6, fontFamily: F }}>
              {t('pages.contact.form_subject')} *
            </label>
            <select
              value={form.subject} onChange={set('subject')}
              style={{ ...inputStyle(errors.subject), cursor: 'pointer' }}
            >
              <option value="">{t('pages.contact.form_subject_ph')}</option>
              {(t('pages.contact.subjects', { returnObjects: true }) || []).map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            {errors.subject && <div style={{ color: '#ff8080', fontSize: 11, marginTop: 4 }}>{errors.subject}</div>}
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', color: 'rgba(210,195,250,0.75)', fontSize: 12, letterSpacing: 1, marginBottom: 6, fontFamily: F }}>
              {t('pages.contact.form_message')} *
            </label>
            <textarea
              value={form.message} onChange={set('message')}
              maxLength={2000} rows={6}
              placeholder={t('pages.contact.form_message_ph')}
              style={{ ...inputStyle(errors.message), resize: 'vertical', minHeight: 140 }}
              onFocus={e => e.target.style.borderColor = 'rgba(240,192,64,0.5)'}
              onBlur={e => e.target.style.borderColor = errors.message ? '#ff8080' : 'rgba(255,255,255,0.1)'}
            />
            <div style={{ textAlign: 'right', fontSize: 11, color: 'rgba(210,195,250,0.4)', marginTop: 4 }}>
              {form.message.length}/2000
            </div>
            {errors.message && <div style={{ color: '#ff8080', fontSize: 11, marginTop: 2 }}>{errors.message}</div>}
          </div>

          <Turnstile onToken={setCaptcha} resetRef={captchaResetRef} style={{ justifyContent: 'flex-start', marginBottom: 20 }} />

          <button
            type="submit"
            disabled={status === 'sending' || !captchaOk}
            style={{
              background: status === 'sending' || !captchaOk ? 'rgba(240,192,64,0.3)' : 'linear-gradient(135deg,#d4a017,#f0c040)',
              border: 'none', borderRadius: 6,
              color: '#04020c', padding: '13px 32px',
              fontFamily: F, fontSize: 13, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase',
              cursor: status === 'sending' ? 'not-allowed' : 'pointer',
              transition: 'all .2s',
              boxShadow: '0 0 24px rgba(240,192,64,0.2)',
            }}
          >
            {status === 'sending' ? '⏳ Siunčiama...' : `⚔️ ${t('pages.contact.form_send')}`}
          </button>
        </form>

        {/* Sidebar info */}
        <div>
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 14, padding: '24px 22px', marginBottom: 20,
          }}>
            <div style={{ fontFamily: F, fontSize: 11, color: 'rgba(240,192,64,0.6)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 14 }}>
              {t('pages.contact.email_title')}
            </div>
            <a href="mailto:info@fantastai.lt" style={{ color: '#f0c040', fontSize: 15, textDecoration: 'none', fontWeight: 600 }}>
              info@fantastai.lt
            </a>
            <div style={{ color: 'rgba(210,195,250,0.5)', fontSize: 12, marginTop: 6 }}>
              Atsakome per 1–2 darbo dienas
            </div>
          </div>

          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 14, padding: '24px 22px',
          }}>
            <div style={{ fontFamily: F, fontSize: 11, color: 'rgba(240,192,64,0.6)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 14 }}>
              {t('pages.contact.social_title')}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {socials.map(({ icon, label, href }) => (
                <a key={label} href={href} rel="noopener noreferrer" target="_blank" style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  color: 'rgba(210,195,250,0.75)', fontSize: 14, textDecoration: 'none',
                  transition: 'color .15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.color = '#f0c040'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(210,195,250,0.75)'}
                >
                  <span style={{ fontSize: 20 }}>{icon}</span>
                  {label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PRIVATUMO POLITIKA
// ─────────────────────────────────────────────────────────────────────────────
export function PrivacyPage() {
  const { t } = useTranslation();

  const sections = [
    {
      title: '1. Bendroji informacija',
      text: `Ši privatumo politika aprašo, kaip Fantastai.lt (toliau – „Portalas") renka, naudoja ir saugo jūsų asmens duomenis, kai naudojatės mūsų paslaugomis.\n\nPortalas laikosi 2016 m. balandžio 27 d. Europos Parlamento ir Tarybos reglamento (ES) 2016/679 (BDAR/GDPR) reikalavimų.`,
    },
    {
      title: '2. Kokie duomenys renkami',
      text: `Registracijos metu: vardas, el. pašto adresas, pasirinktas vaidmuo.\n\nNaudojimosi metu: IP adresas (logams), naršymo statistika (anonimizuota), slapukai (žr. slapukų politiką).\n\nSusisiekus su mumis: jūsų el. paštas, žinutės turinys.`,
    },
    {
      title: '3. Kaip naudojame duomenis',
      text: `Jūsų duomenis naudojame:\n• Paskyros sukūrimui ir valdymui\n• El. pašto patvirtinimui ir MFA\n• Ryšiui su jumis (atsakant į klausimus, siunčiant svarbius pranešimus)\n• Portalo saugumo užtikrinimui\n\nDuomenų neparduodame ir nepateikiame trečiosioms šalims komerciniais tikslais.`,
    },
    {
      title: '4. Slapukai (Cookies)',
      text: `Naudojame tik būtinus funkcinius slapukus (sesijos, autentifikacijos). Neprivalomus analitinius slapukus įdiegiame tik gavę jūsų sutikimą.`,
    },
    {
      title: '5. Jūsų teisės',
      text: `Pagal BDAR turite teisę:\n• Susipažinti su savo duomenimis\n• Reikalauti duomenų ištaisymo\n• Reikalauti duomenų ištrynimo (teisė būti pamirštam)\n• Nesutikti su duomenų tvarkymu\n• Pateikti skundą priežiūros institucijai (Valstybinė duomenų apsaugos inspekcija, vdai.lrv.lt)`,
    },
    {
      title: '6. Duomenų saugumas',
      text: `Slaptažodžiai saugomi naudojant bcrypt maišos algoritmą. Naudojame HTTPS protokolą. Prieiga prie duomenų bazės apribota. Reguliariai atliekami saugumo auditai.`,
    },
    {
      title: '7. Kontaktai',
      text: `Klausimais dėl asmens duomenų tvarkymo kreipkitės: info@fantastai.lt`,
    },
  ];

  return (
    <PageShell title={t('pages.privacy.title')} subtitle={`${t('pages.privacy.updated')}: 2025-01-01`} icon="🔒">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
        {sections.map(({ title, text }) => (
          <div key={title}>
            <h2 style={{ fontFamily: F, fontSize: 15, color: '#ffffff', margin: '0 0 12px', letterSpacing: 1 }}>{title}</h2>
            {text.split('\n').map((line, i) => (
              <p key={i} style={{ color: 'rgba(210,195,250,0.8)', fontSize: 14, lineHeight: 1.8, margin: '0 0 6px' }}>
                {line}
              </p>
            ))}
          </div>
        ))}
      </div>
    </PageShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// NAUDOJIMO TAISYKLĖS
// ─────────────────────────────────────────────────────────────────────────────
export function TermsPage() {
  const { t } = useTranslation();

  const sections = [
    {
      title: '1. Bendrosios nuostatos',
      text: `Naudodamiesi Fantastai.lt portalu, sutinkate laikytis šių naudojimo taisyklių. Portalo operatorius pasilieka teisę keisti taisykles apie tai pranešdamas el. paštu arba portale.`,
    },
    {
      title: '2. Registracija ir paskyra',
      text: `• Registracija galima sulaukus 16 metų.\n• Kiekvienam asmeniui leidžiama turėti vieną paskyrą.\n• Privalote saugoti savo prisijungimo duomenis ir neatskleisti jų tretiesiems asmenims.\n• Portalas neatsako už nuostolius, kilusius dėl nepagarbaus savo paskyros apsaugojimo.`,
    },
    {
      title: '3. Draudžiamas turinys',
      text: `Draudžiama skelbti:\n• Neapykantą kurstantį, diskriminacinį ar smurtinį turinį\n• Šlamštą, masinio siuntimo reklamas\n• Kito asmens asmens duomenis be jo sutikimo\n• Autorių teisių saugomą turinį be leidimo\n• Kenkėjiškas nuorodas ar kodą`,
    },
    {
      title: '4. Klubai ir renginiai',
      text: `Partneriai ir klubų savininkais gali skelbti klubus ir renginius. Informacija turi būti tiksli ir aktuali. Portalas pasilieka teisę pašalinti neteisingą ar klaidinančią informaciją.`,
    },
    {
      title: '5. Atsakomybė',
      text: `Portalas neatsako už:\n• Trečiųjų šalių svetainių turinį\n• Vartotojų skelbiamą turinį\n• Renginių organizavimo kokybę\n• Techninius sutrikimus, kilusius dėl nenugalimų aplinkybių`,
    },
    {
      title: '6. Intelektinė nuosavybė',
      text: `Portalo dizainas, logotipas ir originalus turinys priklauso Fantastai.lt. Vartotojų skelbtas turinys išlieka jų nuosavybe, tačiau jie suteikia portalui licenciją jį rodyti.`,
    },
    {
      title: '7. Taikytina teisė',
      text: `Šioms taisyklėms taikoma Lietuvos Respublikos teisė. Ginčai sprendžiami Lietuvos Respublikos teismuose.`,
    },
  ];

  return (
    <PageShell title={t('pages.terms.title')} subtitle={`${t('pages.terms.updated')}: 2025-01-01`} icon="📜">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
        {sections.map(({ title, text }) => (
          <div key={title}>
            <h2 style={{ fontFamily: F, fontSize: 15, color: '#ffffff', margin: '0 0 12px', letterSpacing: 1 }}>{title}</h2>
            {text.split('\n').map((line, i) => (
              <p key={i} style={{ color: 'rgba(210,195,250,0.8)', fontSize: 14, lineHeight: 1.8, margin: '0 0 6px' }}>
                {line}
              </p>
            ))}
          </div>
        ))}
      </div>
    </PageShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// KAINORAŠTIS
// ─────────────────────────────────────────────────────────────────────────────
export function PricingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const plans = [
    {
      key: 'free',
      price: t('pages.pricing.free_price'),
      label: t('pages.pricing.free'),
      per: '',
      color: 'rgba(210,195,250,0.5)',
      border: 'rgba(255,255,255,0.1)',
      cta: t('pages.pricing.cta_free'),
      ctaAction: () => navigate('/'),
      features: [
        '✅ Profilio sukūrimas',
        '✅ Renginio registracija',
        '✅ Atsiliepimų rašymas',
        '✅ Kalendoriaus peržiūra',
        '✅ Naujienų skaitymas',
        '❌ Klubo registracija',
        '❌ Renginio skelbimas',
        '❌ Reklaminis pozicionavimas',
      ],
    },
    {
      key: 'partner',
      price: t('pages.pricing.partner_price'),
      label: t('pages.pricing.partner'),
      per: t('pages.pricing.per_month'),
      color: '#f0c040',
      border: 'rgba(240,192,64,0.5)',
      popular: true,
      cta: t('pages.pricing.cta_partner'),
      ctaAction: () => navigate('/contact'),
      features: [
        '✅ Visos nemokamo plano funkcijos',
        '✅ Klubo registracija ir valdymas',
        '✅ Renginių skelbimas (iki 4/mėn.)',
        '✅ Partnerio ženklelis',
        '✅ Statistikos prieiga',
        '✅ Prioritetinis palaikymas el. paštu',
        '❌ Premium pozicionavimas',
        '❌ Reklaminis skydelis',
      ],
    },
    {
      key: 'premium',
      price: t('pages.pricing.premium_price'),
      label: t('pages.pricing.premium'),
      per: t('pages.pricing.per_month'),
      color: '#c8b4ff',
      border: 'rgba(155,118,216,0.5)',
      cta: t('pages.pricing.cta_premium'),
      ctaAction: () => navigate('/contact'),
      features: [
        '✅ Visos partnerio plano funkcijos',
        '✅ Neriboti renginiai',
        '✅ Pirmoji pozicija paieškoje',
        '✅ Reklaminis skydelis portale',
        '✅ Straipsniai naujienų sekcijoje',
        '✅ Socialinių tinklų kryžminė reklama',
        '✅ Mėnesinė statistikos ataskaita',
        '✅ Asmeninis vadybininkas',
      ],
    },
  ];

  return (
    <PageShell title={t('pages.pricing.title')} subtitle={t('pages.pricing.subtitle')} icon="💎">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20 }}>
        {plans.map(({ key, price, label, per, color, border, popular, cta, ctaAction, features }) => (
          <div key={key} style={{
            background: popular
              ? 'linear-gradient(180deg,rgba(240,192,64,0.07),rgba(123,79,212,0.05))'
              : 'rgba(255,255,255,0.02)',
            border: `1px solid ${border}`,
            borderRadius: 16, padding: '28px 24px',
            position: 'relative',
            display: 'flex', flexDirection: 'column',
            transform: popular ? 'scale(1.03)' : 'none',
            boxShadow: popular ? '0 0 40px rgba(240,192,64,0.12)' : 'none',
          }}>
            {popular && (
              <div style={{
                position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                background: 'linear-gradient(135deg,#d4a017,#f0c040)',
                color: '#04020c', fontSize: 10, fontWeight: 800,
                padding: '4px 14px', borderRadius: 20, fontFamily: F, letterSpacing: 1.5,
                whiteSpace: 'nowrap',
              }}>
                {t('pages.pricing.most_popular')}
              </div>
            )}

            <div style={{ fontFamily: F, fontSize: 12, color: color, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 8 }}>
              {label}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 20 }}>
              <span style={{ fontFamily: F, fontSize: 38, fontWeight: 900, color }}>
                {price}
              </span>
              {per && <span style={{ color: 'rgba(210,195,250,0.5)', fontSize: 13 }}>{per}</span>}
            </div>

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
              {features.map((f, i) => (
                <div key={i} style={{ fontSize: 13, color: f.startsWith('❌') ? 'rgba(210,195,250,0.35)' : 'rgba(210,195,250,0.85)', lineHeight: 1.4 }}>
                  {f}
                </div>
              ))}
            </div>

            <button
              onClick={ctaAction}
              style={{
                background: popular ? 'linear-gradient(135deg,#d4a017,#f0c040)' : 'rgba(255,255,255,0.07)',
                border: `1px solid ${border}`,
                borderRadius: 8, color: popular ? '#04020c' : color,
                padding: '12px', fontFamily: F, fontSize: 11, fontWeight: 700,
                letterSpacing: 1.5, textTransform: 'uppercase', cursor: 'pointer',
                transition: 'all .2s',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              {cta}
            </button>
          </div>
        ))}
      </div>

      <Divider />

      {/* FAQ */}
      <SectionTitle>Dažniausiai užduodami klausimai</SectionTitle>
      {[
        ['Ar yra nemokamas bandomasis laikotarpis?', 'Taip — partnerio planą galite išbandyti 14 dienų nemokamai. Jokios kortelės nereikia.'],
        ['Ar galiu keisti planą?', 'Taip, planą galite keisti bet kada. Mokėjimas perskaičiuojamas proporcingai.'],
        ['Kaip vykdomi mokėjimai?', 'Mokėjimai apdorojami Stripe platforma. Priimame visas pagrindinias mokėjimo korteles ir SEPA pervedimus.'],
        ['Ką daryti, jei turiu klausimų?', 'Rašykite mums info@fantastai.lt arba naudokitės kontaktų forma.'],
      ].map(([q, a]) => (
        <div key={q} style={{ marginBottom: 20 }}>
          <h3 style={{ fontFamily: F, fontSize: 13, color: '#ffffff', margin: '0 0 8px', letterSpacing: 0.5 }}>{q}</h3>
          <p style={{ color: 'rgba(210,195,250,0.7)', fontSize: 13, lineHeight: 1.7, margin: 0 }}>{a}</p>
        </div>
      ))}
    </PageShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MEDIJŲ RINKINYS
// ─────────────────────────────────────────────────────────────────────────────
export function MediaKitPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const stats = [
    { n: '1 200+', label: 'Mėnesiniai aktyvūs vartotojai' },
    { n: '48+', label: 'Registruotų klubų' },
    { n: '124+', label: 'Metinių renginių' },
    { n: '72%', label: 'Vartotojų 18–35 metai' },
    { n: '88%', label: 'Iš Lietuvos' },
    { n: '3.4 min', label: 'Vidutinis apsilankymo laikas' },
  ];

  const assets = [
    { icon: '🖼️', title: 'Logotipai (SVG + PNG)', desc: 'Šviesiam ir tamsiam fonui' },
    { icon: '🎨', title: 'Spalvų paletė', desc: 'Hex, RGB, CMYK' },
    { icon: '📐', title: 'Stiliaus gidas', desc: 'Tipografija, tarpai, ikonos' },
    { icon: '🖼️', title: 'Reklaminiai skydeliai', desc: '300×250, 728×90, 160×600' },
  ];

  return (
    <PageShell title={t('pages.media.title')} subtitle={t('pages.media.subtitle')} icon="📦">
      {/* Audience Stats */}
      <div style={{ marginBottom: 48 }}>
        <SectionTitle>{t('pages.media.stats_title')}</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
          {stats.map(({ n, label }) => (
            <div key={label} style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 10, padding: '18px 16px', textAlign: 'center',
            }}>
              <div style={{ fontFamily: F, fontSize: 26, fontWeight: 900, color: '#f0c040', marginBottom: 6 }}>{n}</div>
              <div style={{ fontSize: 12, color: 'rgba(210,195,250,0.6)', lineHeight: 1.4 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      <Divider />

      {/* Brand assets */}
      <div style={{ marginBottom: 48 }}>
        <SectionTitle>{t('pages.media.assets_title')}</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 14 }}>
          {assets.map(({ icon, title, desc }) => (
            <div key={title} style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 10, padding: '16px 18px',
              display: 'flex', alignItems: 'center', gap: 14,
            }}>
              <div style={{ fontSize: 28, flexShrink: 0 }}>{icon}</div>
              <div>
                <div style={{ color: '#ffffff', fontSize: 14, fontWeight: 600, marginBottom: 3 }}>{title}</div>
                <div style={{ color: 'rgba(210,195,250,0.55)', fontSize: 12 }}>{desc}</div>
              </div>
              <button
                style={{
                  marginLeft: 'auto', flexShrink: 0,
                  background: 'rgba(240,192,64,0.1)', border: '1px solid rgba(240,192,64,0.3)',
                  borderRadius: 6, color: '#f0c040', padding: '5px 12px',
                  fontSize: 11, fontFamily: F, letterSpacing: 1, cursor: 'pointer',
                }}
                onClick={() => navigate('/contact')}
              >
                ↓
              </button>
            </div>
          ))}
        </div>
      </div>

      <Divider />

      {/* Press contact */}
      <div>
        <SectionTitle>{t('pages.media.contact_title')}</SectionTitle>
        <div style={{
          background: 'linear-gradient(135deg,rgba(240,192,64,0.07),rgba(123,79,212,0.07))',
          border: '1px solid rgba(240,192,64,0.2)',
          borderRadius: 14, padding: '28px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 20,
        }}>
          <div>
            <div style={{ fontFamily: F, fontSize: 11, color: 'rgba(240,192,64,0.6)', letterSpacing: 2, marginBottom: 8 }}>
              {t('pages.media.press_email')}
            </div>
            <a href={`mailto:${t('pages.media.press_contact')}`} style={{ color: '#f0c040', fontSize: 18, fontWeight: 700, textDecoration: 'none' }}>
              {t('pages.media.press_contact')}
            </a>
          </div>
          <button
            onClick={() => navigate('/contact')}
            style={{

              background: 'linear-gradient(135deg,#d4a017,#f0c040)',
              border: 'none', borderRadius: 8,
              color: '#04020c', padding: '12px 24px',
              fontFamily: F, fontSize: 12, fontWeight: 800, letterSpacing: 2, cursor: 'pointer',
            }}
          >
            ✉️ SUSISIEKTI
          </button>
        </div>
      </div>
    </PageShell>
  );
}

// ── Email Verification Page ──────────────────────────────────────────────────

export function VerifyEmailPage() {
  const [params]   = useSearchParams();
  const navigate   = useNavigate();
  const [status, setStatus] = useState('loading'); // 'loading' | 'ok' | 'error'
  const [msg,    setMsg]    = useState('');

  useEffect(() => {
    const token = params.get('token');
    if (!token) { setStatus('error'); setMsg('Nėra patvirtinimo rakto URL adrese.'); return; }
    api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(res => {
        setStatus('ok');
        setMsg(res.data.detail || 'Paskyra patvirtinta!');
      })
      .catch(e => {
        setStatus('error');
        setMsg(e?.response?.data?.detail || 'Patvirtinimo nuoroda negalioja arba pasibaigė.');
      });
  }, []); // eslint-disable-line

  return (
    <div className="verify-wrap">
      <div className="verify-card">
        {status === 'loading' && (
          <>
            <div className="verify-icon">⏳</div>
            <div className="verify-title">Tikrinama...</div>
            <div className="verify-text">Prašome palaukti.</div>
          </>
        )}
        {status === 'ok' && (
          <>
            <div className="verify-icon">✅</div>
            <div className="verify-title">Paskyra patvirtinta!</div>
            <div className="verify-text">
              {msg}<br /><br />
              Jūsų paskyra buvo patvirtinta. Dabar galite prisijungti.
            </div>
            <button className="prof-btn prof-btn-primary" onClick={() => navigate('/')}>
              Prisijungti
            </button>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="verify-icon">❌</div>
            <div className="verify-title">Patvirtinimas nepavyko</div>
            <div className="verify-text">{msg}</div>
            <button className="prof-btn prof-btn-secondary" style={{ marginTop: 8 }} onClick={() => navigate('/')}>
              Grįžti į pradžią
            </button>
          </>
        )}
      </div>
    </div>
  );
}
